import { Button } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/popover';
import { useOptionalCollab } from '@examdojo/core/tiptap-react-ui-components/contexts/collab-context';
import { useOptionalUser } from '@examdojo/core/tiptap-react-ui-components/contexts/user-context';
import { useTiptapEditor } from '@examdojo/core/tiptap-react-ui-components/hooks/use-tiptap-editor';
import type { CollabCommentData } from '@examdojo/core/tiptap-react-ui-components/lib/collab-config';
import { hasCommentsKit } from '@examdojo/core/tiptap-react-ui-components/lib/has-comments-kit';
import { type Editor, isTextSelection } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';
import { CommentComposer } from '../comment-composer';

export interface CommentButtonProps {
  editor?: Editor | null;
  hideWhenUnavailable?: boolean;
}

export function CommentButton({ editor: providedEditor, hideWhenUnavailable = false }: CommentButtonProps) {
  const { editor } = useTiptapEditor(providedEditor);
  const user = useOptionalUser();
  const { hasCollab, hasError: collabHasError } = useOptionalCollab();
  const [showInput, setShowInput] = useState(false);

  const available = !!user && hasCollab && hasCommentsKit(editor) && !collabHasError;
  const hasTextSelection = !!editor && isTextSelection(editor.state.selection) && !editor.state.selection.empty;

  const handleOpen = useCallback(() => {
    if (!editor || editor.state.selection.empty) return;
    setShowInput(true);
  }, [editor]);

  const handleClose = useCallback(() => {
    setShowInput(false);
  }, []);

  const handleSubmit = useCallback(
    (content: string) => {
      if (!editor || !content || !user) return;

      editor
        .chain()
        .focus()
        .setThread({
          content,
          commentData: { userName: user.name, userAvatar: user.avatar } satisfies CollabCommentData,
        })
        .run();

      handleClose();
    },
    [editor, user, handleClose],
  );

  useEffect(() => {
    if (!available || !hasTextSelection) {
      setShowInput(false);
    }
  }, [available, hasTextSelection]);

  if (hideWhenUnavailable && !available) return null;
  if (!editor) return null;

  return (
    <Popover
      open={showInput}
      onOpenChange={(open) => {
        if (open) {
          handleOpen();
          return;
        }
        handleClose();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          data-style="ghost"
          role="button"
          tabIndex={-1}
          tooltip="Add comment"
          disabled={!available || !hasTextSelection}
        >
          <svg
            className="tiptap-button-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
          </svg>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="comment-input-popover"
        side="bottom"
        align="start"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <CommentComposer onSubmit={handleSubmit} onClose={handleClose} />
      </PopoverContent>
    </Popover>
  );
}
